// src/pages/TransactionsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { formatCurrency, formatDate } from '../utils/formatting';
import LoadingSpinner from '../components/LoadingSpinner';
import PainSlider from '../components/PainSlider';

const TransactionsPage = () => {
    const {
        pendingTransactions,
        checkPendingTransactions,
        acknowledgeTransaction,
        budgetCategories,
        loadBudgetCategories,
        isCheckingTransactions,
        transactionBlockingEnabled,
        toggleTransactionBlocking
    } = useAppContext();

    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Load data on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                setRefreshing(true);
                setError(null);
                await checkPendingTransactions();
                await loadBudgetCategories();
            } catch (err) {
                setError('Failed to load transaction data. Please try again.');
                console.error('Error loading transaction data:', err);
            } finally {
                setRefreshing(false);
            }
        };

        loadData();
    }, [checkPendingTransactions, loadBudgetCategories]);

    // Handle refresh button
    const handleRefresh = async () => {
        try {
            setRefreshing(true);
            setError(null);
            setSuccess(null);
            await checkPendingTransactions();
            await loadBudgetCategories();
            setSuccess('Transactions refreshed successfully');
        } catch (err) {
            setError('Failed to refresh transactions. Please try again.');
            console.error('Error refreshing transactions:', err);
        } finally {
            setRefreshing(false);
        }
    };

    // Handle transaction selection for acknowledgment
    const handleSelectTransaction = (transaction) => {
        setSelectedTransaction(transaction);
    };

    // Handle transaction acknowledgment
    const handleAcknowledge = async (painLevel, budgetCategory) => {
        if (!selectedTransaction) return;

        try {
            setRefreshing(true);
            setError(null);

            await acknowledgeTransaction(
                selectedTransaction.id,
                painLevel,
                budgetCategory
            );

            setSuccess(`Transaction for ${formatCurrency(selectedTransaction.amount)} acknowledged successfully`);
            setSelectedTransaction(null);

            // Refresh pending transactions
            await checkPendingTransactions();
        } catch (err) {
            setError('Failed to acknowledge transaction. Please try again.');
            console.error('Error acknowledging transaction:', err);
        } finally {
            setRefreshing(false);
        }
    };

    // Handle blocking toggle
    const handleToggleBlocking = () => {
        toggleTransactionBlocking(!transactionBlockingEnabled);
    };

    return (
        <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    Pending Credit Card Transactions
                </h1>

                <div className="flex gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                    >
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>

                    <Link
                        to="/"
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                        Back to Dashboard
                    </Link>
                </div>
            </div>

            {/* Success/Error messages */}
            {error && (
                <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-md">
                    {error}
                </div>
            )}

            {success && (
                <div className="p-4 mb-6 bg-green-100 text-green-700 rounded-md">
                    {success}
                </div>
            )}

            {/* Feature settings */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Transaction Pain Settings</h2>

                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-gray-700">
                            When enabled, this feature requires you to acknowledge all credit card transactions
                            before accessing other parts of the app, helping make spending more tangible.
                        </p>
                    </div>

                    <div className="ml-4">
                        <label className="inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={transactionBlockingEnabled}
                                onChange={handleToggleBlocking}
                                className="sr-only peer"
                            />
                            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            <span className="ml-3 text-sm font-medium text-gray-900">
                                {transactionBlockingEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Pending transactions list */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">
                    Pending Transactions ({pendingTransactions.length})
                </h2>

                {isCheckingTransactions ? (
                    <div className="flex justify-center py-10">
                        <LoadingSpinner size="medium" message="Loading transactions..." />
                    </div>
                ) : pendingTransactions.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">Merchant</th>
                                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingTransactions.map((transaction) => (
                                    <tr key={transaction.id}>
                                        <td className="py-3 px-4 border-b border-gray-200">
                                            {formatDate(transaction.date)}
                                        </td>
                                        <td className="py-3 px-4 border-b border-gray-200">
                                            {transaction.merchant}
                                        </td>
                                        <td className="py-3 px-4 border-b border-gray-200">
                                            {transaction.category}
                                        </td>
                                        <td className="py-3 px-4 border-b border-gray-200 text-right font-mono text-red-600">
                                            {formatCurrency(transaction.amount)}
                                        </td>
                                        <td className="py-3 px-4 border-b border-gray-200 text-center">
                                            <button
                                                onClick={() => handleSelectTransaction(transaction)}
                                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                            >
                                                Acknowledge
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-10 text-gray-500">
                        <p>No pending transactions to acknowledge.</p>
                        <p className="mt-2">
                            New credit card transactions will appear here when they are detected.
                        </p>
                    </div>
                )}
            </div>

            {/* Pain slider modal for selected transaction */}
            {selectedTransaction && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">
                                Acknowledge Transaction
                            </h2>

                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <span className="text-sm text-gray-600">Merchant:</span>
                                        <div className="font-semibold">{selectedTransaction.merchant}</div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-600">Amount:</span>
                                        <div className="font-semibold text-red-600">
                                            {formatCurrency(selectedTransaction.amount)}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-600">Date:</span>
                                        <div>{formatDate(selectedTransaction.date)}</div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-600">Category:</span>
                                        <div>{selectedTransaction.category}</div>
                                    </div>
                                </div>
                            </div>

                            <PainSlider
                                amount={selectedTransaction.amount}
                                onComplete={handleAcknowledge}
                                onCancel={() => setSelectedTransaction(null)}
                                budgetCategories={budgetCategories}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransactionsPage;